import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '@/auth/useAccessToken';
import { streamSse } from '@/lib/sseClient';

/**
 * Subscribes to a ticket's Server-Sent Events stream and invalidates the cached
 * detail + list queries whenever the server signals activity.
 *
 * The SSE event is a lightweight poke (IDs only, no message body), so the
 * refetch through the authorized `/api/tickets/{n}` endpoint is what actually
 * loads data — row-level security and internal-note filtering stay enforced
 * server-side. This replaces the SignalR client we never shipped.
 *
 * Pass `ticketId` (the GUID from the detail payload) once it's available; the
 * hook is a no-op until then, so it's safe to call before the query resolves.
 */
export function useTicketEvents(ticketId: string | undefined, ticketNumber: number): void {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();

  // useAccessToken returns a fresh closure each render; capture it in a ref so
  // it doesn't churn the effect (which would reconnect the stream constantly).
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  useEffect(() => {
    if (!ticketId) return;

    const controller = new AbortController();

    void streamSse(`/api/tickets/${ticketId}/events`, {
      getAccessToken: () => getAccessTokenRef.current(),
      signal: controller.signal,
      onEvent: () => {
        // Any change (message, assignment, status, watcher) → refetch the views
        // that could reflect it. The event is an id-only poke; the authorized
        // refetch is what actually loads data.
        void queryClient.invalidateQueries({ queryKey: ['tickets', 'detail', ticketNumber] });
        void queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] });
        void queryClient.invalidateQueries({ queryKey: ['tickets', 'queue'] });
      }
    });

    return () => controller.abort();
  }, [ticketId, ticketNumber, queryClient]);
}
